import { CSSProperties } from 'react';
import React from 'react';

import { Box, BoxProps, Flex, Text, keyframes } from '@chakra-ui/react';
import { Card, League, MLBStats, NBAStats, NFLStats, PlayerStats, WNBAStats } from 'models';
import unidecode from 'unidecode';

import { useAppSelector } from 'hooks/app';

const shineAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;
interface FaceProps extends BoxProps {
  calcWidth: string;
  isLoading?: boolean;
  player?: PlayerStats | null;
  card?: Card | null;
  aspectRatio?: number;
  isOriginalImageUploaded: boolean;
  width?: string;
  detailed?: boolean;
  league?: League;
  styles?: {
    image?: CSSProperties;
    info?: CSSProperties;
    firstName?: CSSProperties;
    lastName?: CSSProperties;
  };
}

export const Back = (props: FaceProps) => {
  const {
    aspectRatio = 1 / 1.5,
    calcWidth,
    styles,
    isOriginalImageUploaded,
    detailed,
    player,
    card,
    isLoading,
    league,
  } = props;
  const leagueData = useAppSelector((state) => state.league.league);

  const getLeagueBySrId = (srId?: string | null) => {
    if (!srId) return;
    return leagueData?.results.find((team) => {
      return team.sportradarId === srId;
    });
  };

  const resolveTeam = (() => {
    const teamName = !card?.isHomeTeam
      ? getLeagueBySrId(card?.awayTeamSrId)?.name
      : getLeagueBySrId(card?.homeTeamSrId)?.name;
    return teamName;
  })();

  const mapServerStatsToPlayerStats = (serverStats: PlayerStats, league?: League) => {
    const nbaStatMapping: Record<string, string> = {
      averagePoints: 'POINTS',
      averageRebounds: 'REBOUNDS',
      averageAssists: 'ASSISTS',
      averageBlocks: 'BLOCKS',
      averageSteals: 'STEALS',
      averageThreePointsMade: '3-POINTERS',
      averageMinutes: 'MINUTES',
      fieldGoalPercentage: 'FIELD GOAL %',
    };

    const wnbaStatMapping: Record<string, string> = {
      averagePoints: 'POINTS',
      averageRebounds: 'REBOUNDS',
      averageAssists: 'ASSISTS',
      averageBlocks: 'BLOCKS',
      averageSteals: 'STEALS',
      averageThreePointsMade: '3-POINTERS',
      averagePersonalFouls: 'FOULS',
      averageTurnovers: 'TURNOVERS',
    };

    const nflStatMapping: Record<string, string> = {
      gamesPlayed: 'GAMES',
      passingYards: 'PASS YDS',
      rushingYards: 'RUSH YDS',
      receivingYards: 'REC YDS',
      receivingReceptions: 'RECEPTIONS',
      rushingTouchdowns: 'RUSH TD',
      receivingTouchdowns: 'REC TD',
      passingTouchdowns: 'PASS TD',

      // passingInterceptions: 'INT',
      // passingAttempts: 'PASS ATT',
      // passingCompletions: 'PASS COMP',
      // passingCmpPct: 'COMP %',
      // passingRating: 'PASSER RATING',
      // passingLongest: 'LONGEST PASS',
      // rushingAttempts: 'RUSH ATT',
      // rushingAvgYards: 'RUSH AVG',
      // rushingLongest: 'LONGEST RUSH',
      // receivingTargets: 'TARGETS',
      // receivingLongest: 'LONGEST REC',
      // receivingYardsAfterContact: 'YARDS AFTER CONTACT',
      // defenseTackles: 'TACKLES',
      // defenseCombined: 'COMBINED TACKLES',
      // defenseTloss: 'TACKLES FOR LOSS',
      // defensePassesDefended: 'PASSES DEFENDED',
      // defenseQbHits: 'QB HITS',
      // defenseSacks: 'SACKS',
      // defenseInterceptions: 'INTERCEPTIONS',
      // defenseForcedFumbles: 'FORCED FUMBLES',
      // defenseFumbleRecoveries: 'FUMBLE RECOVERIES',
      // fieldGoalsMade: 'FIELD GOALS',
      // extraPointsMade: 'EXTRA POINTS',
      // fieldGoalsLongest: 'LONGEST FG',
      // puntsAttempts: 'PUNTS',
      // puntsAvgYards: 'PUNT AVG',
      // puntsLongest: 'LONGEST PUNT',
      // puntsInside20: 'PUNTS INSIDE 20',
      // puntsTouchbacks: 'PUNT TOUCHBACKS',
    };

    const mlbStatMapping: Record<string, string> = {
      hittingGamesPlay: 'GAMES',
      hittingAb: 'AT BATS',
      hittingOnbaseTb: 'TOTAL BASES',
      hittingOnbaseHits: 'TOTAL HITS',
      hittingOnbaseD: 'DOUBLES',
      hittingOnbaseT: 'TRIPLES',
      hittingOnbaseHr: 'HOME RUNS',
      hittingRunsTotal: 'RUNS/RBI',
    };

    const mlbStatOrder = [
      'GAMES',
      'AT BATS',
      'TOTAL BASES',
      'TOTAL HITS',
      'DOUBLES',
      'TRIPLES',
      'HOME RUNS',
      'RUNS/RBI',
    ];

    const wnbaStatOrder = ['POINTS', 'REBOUNDS', 'ASSISTS', 'BLOCKS', 'STEALS', '3-POINTERS', 'FOULS', 'TURNOVERS'];

    const nbaStatOrder = [
      'POINTS',
      'REBOUNDS',
      'ASSISTS',
      'BLOCKS',
      'STEALS',
      '3-POINTERS',
      'MINUTES',
      'FIELD GOAL %',
    ];

    let statMapping: Record<string, string>;
    let mappedStats: Array<{ title: string; value: number | string }> = [];

    if (league === 'nfl') {
      statMapping = nflStatMapping;
      const nflStats = serverStats as NFLStats;
      mappedStats = Object.entries(nflStatMapping).map(([key, value]) => ({
        title: value,
        value: nflStats[key as keyof NFLStats] || '--',
      }));
    } else if (league === 'mlb') {
      statMapping = mlbStatMapping;
      const mlbStats = serverStats as MLBStats;
      const runsRbi = `${mlbStats.hittingRunsTotal}/${mlbStats.hittingRbi}`;
      mappedStats = Object.entries(statMapping).map(([key, value]) => ({
        title: value,
        value: key === 'hittingRunsTotal' ? runsRbi : mlbStats[key as keyof MLBStats] || '--',
      }));

      mappedStats.sort((a, b) => {
        const aIndex = mlbStatOrder.indexOf(a.title);
        const bIndex = mlbStatOrder.indexOf(b.title);
        return aIndex - bIndex;
      });
    } else if (league === 'wnba') {
      statMapping = wnbaStatMapping;
      const wnbaStats = serverStats as WNBAStats;

      const formatNumber = (num: number | undefined): string => {
        if (num === undefined || num === null) return '--';
        return Number.isInteger(num) ? num.toString() : num.toFixed(1);
      };

      mappedStats = Object.entries(wnbaStatMapping).map(([key, value]) => ({
        title: value,
        value: formatNumber(wnbaStats[key as keyof WNBAStats]),
      }));

      mappedStats.sort((a, b) => {
        const aIndex = wnbaStatOrder.indexOf(a.title);
        const bIndex = wnbaStatOrder.indexOf(b.title);
        return aIndex - bIndex;
      });
    } else {
      statMapping = nbaStatMapping;
      const nbaStats = serverStats as NBAStats;
      mappedStats = Object.entries(nbaStats)
        .filter(([key]) => statMapping[key])
        .map(([key, value]) => ({
          title: statMapping[key],
          value: value,
        }));

      mappedStats.sort((a, b) => {
        const aIndex = nbaStatOrder.indexOf(a.title);
        const bIndex = nbaStatOrder.indexOf(b.title);
        return aIndex - bIndex;
      });

      mappedStats = mappedStats.slice(0, 8);
    }

    return mappedStats;
  };

  const getDefaultStats = (league?: League) => {
    if (league === 'nfl') {
      return [
        { title: 'GAMES', value: 0 },
        { title: 'PASS YDS', value: 0 },
        { title: 'RUSH YDS', value: 0 },
        { title: 'REC YDS', value: 0 },
        { title: 'RECEPTIONS', value: 0 },
        { title: 'RUSH TD', value: 0 },
        { title: 'REC TD', value: 0 },
        { title: 'PASS TD', value: 0 },
      ];
    } else if (league === 'mlb') {
      return [
        { title: 'GAMES', value: 0 },
        { title: 'AT BATS', value: 0 },
        { title: 'TOTAL BASES', value: 0 },
        { title: 'TOTAL HITS', value: 0 },
        { title: 'DOUBLES', value: 0 },
        { title: 'TRIPLES', value: 0 },
        { title: 'HOME RUNS', value: 0 },
        { title: 'RUNS/RBI', value: `${0}/${0}` },
      ];
    } else {
      return [
        { title: 'POINTS', value: 0 },
        { title: 'REBOUNDS', value: 0 },
        { title: 'ASSISTS', value: 0 },
        { title: 'BLOCKS', value: 0 },
        { title: 'STEALS', value: 0 },
        { title: '3-POINTERS', value: 0 },
        { title: 'MINUTES', value: 0 },
        { title: 'FIELD GOAL %', value: 0 },
      ];
    }
  };

  const playerStats = player ? mapServerStatsToPlayerStats(player, league) : getDefaultStats(league);

  if (isLoading) {
    return (
      <>
        <Flex
          borderRadius={`${16 * aspectRatio}px`}
          pos="absolute"
          width="100%"
          height="100%"
          bg="#fff"
          top="0"
          left="0"
          zIndex="10"
          p={`${13 * aspectRatio}px`}
          flexDirection="column"
          gap={`${16 * aspectRatio}px`}
        >
          <Flex
            w="100%"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={`${3 * aspectRatio}px`}
          >
            <Box
              backgroundPosition="center center"
              borderRadius="50%"
              width={`${50 * aspectRatio}px`}
              aspectRatio="1/1"
              position="relative"
              backgroundImage="linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)"
              backgroundRepeat="repeat"
              backgroundSize="200% 100%"
              animation={`${shineAnimation} 1.5s infinite linear`}
            />
            <Flex flexDirection="column" alignItems="center" gap={`${3 * aspectRatio}px`}>
              <Text
                variant="antonioFont"
                height={`${20 * aspectRatio}px`}
                w={`${100 * aspectRatio}px`}
                borderRadius={`${8 * aspectRatio}px`}
                backgroundImage="linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)"
                backgroundRepeat="repeat"
                backgroundSize="200% 100%"
                animation={`${shineAnimation} 1.5s infinite linear`}
              ></Text>
              <Text
                variant="antonioFont"
                fontSize={`${13 * aspectRatio}px`}
                letterSpacing={`-${0.8 * aspectRatio}px`}
                lineHeight="1"
                textColor="inherit"
                color="#9B9292"
                height={`${13 * aspectRatio}px`}
                w={`${60 * aspectRatio}px`}
                borderRadius={`${5 * aspectRatio}px`}
                backgroundImage="linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)"
                backgroundRepeat="repeat"
                backgroundSize="200% 100%"
                animation={`${shineAnimation} 1.5s infinite linear`}
              ></Text>
            </Flex>
          </Flex>

          <Flex flexDirection="column" w="100%" gap={`${5 * aspectRatio}px`}>
            {playerStats.map((stat, index) => (
              <React.Fragment key={stat.value + '_' + index}>
                <Flex justifyContent="space-between">
                  <Text
                    variant="teamName"
                    textTransform="uppercase"
                    fontSize={`${11 * aspectRatio}px`}
                    lineHeight="1"
                    color="rgba(88, 80, 80, 1)"
                    letterSpacing="4%"
                    height={`${11 * aspectRatio}px`}
                    w={`${60 * aspectRatio}px`}
                    borderRadius={`${4 * aspectRatio}px`}
                    backgroundImage="linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)"
                    backgroundRepeat="repeat"
                    backgroundSize="200% 100%"
                    animation={`${shineAnimation} 1.5s infinite linear`}
                  ></Text>
                  <Text
                    variant="teamName"
                    textTransform="uppercase"
                    fontSize={`${11 * aspectRatio}px`}
                    lineHeight="1"
                    color="rgba(88, 80, 80, 1)"
                    letterSpacing="4%"
                    height={`${11 * aspectRatio}px`}
                    w={`${40 * aspectRatio}px`}
                    borderRadius={`${4 * aspectRatio}px`}
                    backgroundImage="linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)"
                    backgroundRepeat="repeat"
                    backgroundSize="200% 100%"
                    animation={`${shineAnimation} 1.5s infinite linear`}
                  ></Text>
                </Flex>
                {index !== playerStats.length - 1 && (
                  <Box w="100%" h="1px" borderBottom="1px dashed rgba(88, 80, 80, 0.2)" />
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex
        borderRadius={`${16 * aspectRatio}px`}
        pos="absolute"
        width="100%"
        height="100%"
        bg="#fff"
        top="0"
        left="0"
        zIndex="10"
        p={`${13 * aspectRatio}px`}
        flexDirection="column"
        gap={`${16 * aspectRatio}px`}
      >
        <Flex w="100%" justifyContent="center" alignItems="center" flexDirection="column" gap={`${3 * aspectRatio}px`}>
          <Box
            backgroundPosition="center center"
            backgroundRepeat="no-repeat"
            backgroundSize="cover"
            borderRadius="50%"
            backgroundImage={`url(${isOriginalImageUploaded ? card?.headshotThumbnails?.original : card?.headshotThumbnails?.small})`}
            backgroundColor={getLeagueBySrId(card?.homeTeamSrId)?.color || '#F83C3C'}
            width={`${50 * aspectRatio}px`}
            // height={`${50 * aspectRatio}px`}
            aspectRatio="1/1"
            position="relative"
          />
          <Flex flexDirection="column" alignItems="center" gap={`${3 * aspectRatio}px`}>
            <Text
              variant="antonioFont"
              fontSize={`${18 * aspectRatio}px`}
              letterSpacing={`-${0.8 * aspectRatio}px`}
              lineHeight="1.2"
              textTransform="uppercase"
              textColor="inherit"
              color="#9B9292"
            >
              {league === 'mlb' && card?.playerAbbrName
                ? `${unidecode(card.playerAbbrName)} ${unidecode(card.playerLastName || '')}`
                : `${unidecode(card?.playerFirstName || '')} ${unidecode(card?.playerLastName || '')}`}
            </Text>
            <Text
              variant="antonioFont"
              fontSize={`${13 * aspectRatio}px`}
              letterSpacing={`-${0.8 * aspectRatio}px`}
              lineHeight="1"
              textColor="inherit"
              color="#9B9292"
            >
              {unidecode(resolveTeam || '')} · {unidecode(card?.playerPrimaryPosition || '')}
            </Text>
          </Flex>
        </Flex>

        <Flex flexDirection="column" w="100%" gap={`${5 * aspectRatio}px`}>
          {playerStats.map((stat, index) => (
            <React.Fragment key={stat.value + '_' + index}>
              <Flex justifyContent="space-between">
                <Text
                  variant="teamName"
                  textTransform="uppercase"
                  fontSize={`${11 * aspectRatio}px`}
                  lineHeight="1"
                  color="rgba(88, 80, 80, 1)"
                  letterSpacing="4%"
                >
                  {stat.title}
                </Text>
                <Text
                  variant="teamName"
                  textTransform="uppercase"
                  fontSize={`${11 * aspectRatio}px`}
                  lineHeight="1"
                  color="rgba(88, 80, 80, 1)"
                  letterSpacing="4%"
                >
                  {stat.value}
                </Text>
              </Flex>
              {index !== playerStats.length - 1 && (
                <Box w="100%" h="1px" borderBottom="1px dashed rgba(88, 80, 80, 0.2)" />
              )}
            </React.Fragment>
          ))}
        </Flex>
      </Flex>
    </>
  );
};
